/**
 * useNotificationsHTTP Hook
 * HTTP-only integration for notifications
 * Replaces WebSocket-based useUniversalNotifications for HTTP-first approach
 * 
 * According to HTTP-First UI Integration documentation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import lib from '../assets/utils/lib';

/**
 * HTTP-only notifications hook
 * @param {Object} options
 * @param {string|null} options.token - JWT token
 * @param {boolean} options.enabled - Whether to fetch notifications
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 30000)
 * @param {number} options.pageSize - Default page size (default: 20)
 */
export function useNotificationsHTTP(options = {}) {
  const {
    token,
    enabled = true,
    refreshInterval = 30000,
    pageSize = 20,
  } = options;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  const refreshIntervalRef = useRef(null);
  const cancelledRef = useRef(false);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async (params = {}) => {
    // Double-check token and enabled state before making API call
    if (!token || !enabled) {
      console.log('[useNotificationsHTTP] Skipping fetch - no token or disabled');
      return;
    }
    
    if (cancelledRef.current) {
      console.log('[useNotificationsHTTP] Already cancelled, skipping fetch');
      return;
    }

    console.log('[useNotificationsHTTP] Fetching notifications from /v2/notifications-v2/');
    setLoading(true);
    try {
      const response = await lib.getNotificationsV2({
        page: 1,
        pageSize,
        ...params,
      });

      if (cancelledRef.current) {
        console.log('[useNotificationsHTTP] Request cancelled');
        return;
      }

      const body = response?.data || response;

      if (parseInt(body?.error) === 0) {
        // Handle both response formats
        const notificationsList = body?.data?.notifications || body?.data || [];
        const notificationsArray = Array.isArray(notificationsList) ? notificationsList : [];
        console.log('[useNotificationsHTTP] Notifications fetched successfully:', notificationsArray.length, 'notifications');
        setNotifications(notificationsArray);
        
        // Update pagination if provided
        if (body?.pagination) {
          setPagination(body.pagination);
        }
        
        // Update unread count if provided
        if (body?.unread_count !== undefined) {
          setUnreadCount(body.unread_count);
        }
        
        setError(null);
      } else {
        throw new Error(body?.message || 'Failed to fetch notifications');
      }
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('[useNotificationsHTTP] Error fetching notifications:', err);
        setError(err);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [token, enabled, pageSize]);

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    // Double-check token and enabled state before making API call
    if (!token || !enabled) {
      console.log('[useNotificationsHTTP] Skipping unread count fetch - no token or disabled');
      return;
    }
    
    if (cancelledRef.current) {
      console.log('[useNotificationsHTTP] Already cancelled, skipping unread count fetch');
      return;
    }

    console.log('[useNotificationsHTTP] Fetching unread count from /v2/notifications-v2/unread-count');
    try {
      const response = await lib.getUnreadNotificationCount();
      
      if (cancelledRef.current) return;

      const body = response?.data || response;

      if (parseInt(body?.error) === 0) {
        const count = body?.unread_count || 0;
        console.log('[useNotificationsHTTP] Unread count fetched:', count);
        setUnreadCount(count);
      }
    } catch (err) {
      if (!cancelledRef.current) {
        console.error('[useNotificationsHTTP] Error fetching unread count:', err);
      }
    }
  }, [token, enabled]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (id) => {
    if (!token || !id) return;

    try {
      await lib.markNotificationAsRead(id);
      
      // Optimistically update local state
      setNotifications(prev =>
        prev.map(n => 
          n.id === id 
            ? { ...n, read_at: new Date().toISOString(), read: true, isRead: true }
            : n
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Refetch unread count to ensure sync
      fetchUnreadCount();
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, [token, fetchUnreadCount]);

  /**
   * Mark all notifications as read
   * @param {Object} options - Optional parameters
   * @param {string} options.category - Optional category filter
   */
  const markAllAsRead = useCallback(async (options = {}) => {
    if (!token) return;

    try {
      const response = await lib.markAllNotificationsAsRead(options);
      const body = response?.data || response;
      
      if (parseInt(body?.error) === 0) {
        // Optimistically update local state
        setNotifications(prev =>
          prev.map(n => ({
            ...n,
            read_at: new Date().toISOString(),
            read: true,
            isRead: true,
          }))
        );
        
        setUnreadCount(0);
        
        // Refetch to ensure sync
        fetchUnreadCount();
        fetchNotifications();
      } else {
        throw new Error(body?.message || 'Failed to mark all notifications as read');
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, [token, fetchNotifications, fetchUnreadCount]);

  /**
   * Delete notification
   * @param {string|number} id - Notification ID
   */
  const deleteNotification = useCallback(async (id) => {
    if (!token || !id) return;

    try {
      const response = await lib.deleteNotification(id);
      const body = response?.data || response;
      
      if (parseInt(body?.error) === 0) {
        // Optimistically remove from local state
        setNotifications(prev => prev.filter(n => n.id !== id));
        
        // Refetch to ensure sync
        fetchNotifications();
        fetchUnreadCount();
      } else {
        throw new Error(body?.message || 'Failed to delete notification');
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, [token, fetchNotifications, fetchUnreadCount]);

  /**
   * Refresh notifications and unread count
   */
  const refresh = useCallback(() => {
    console.log('[useNotificationsHTTP] Polling: Refreshing notifications and unread count');
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Initial fetch and setup HTTP polling
  useEffect(() => {
    console.log('[useNotificationsHTTP] useEffect triggered, token:', token ? 'present' : 'missing', 'enabled:', enabled, 'refreshInterval:', refreshInterval);
    
    if (!token || !enabled) {
      console.log('[useNotificationsHTTP] No token or disabled, clearing state');
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    cancelledRef.current = false;

    // Initial fetch
    console.log('[useNotificationsHTTP] Starting initial fetch');
    fetchNotifications();
    fetchUnreadCount();

    // Setup HTTP polling interval
    if (refreshInterval > 0) {
      console.log('[useNotificationsHTTP] Setting up HTTP polling interval:', refreshInterval, 'ms');
      refreshIntervalRef.current = setInterval(() => {
        if (!cancelledRef.current && token && enabled) {
          console.log('[useNotificationsHTTP] Polling interval triggered');
          fetchNotifications();
          fetchUnreadCount();
        }
      }, refreshInterval);
    }

    return () => {
      console.log('[useNotificationsHTTP] Cleanup: cancelling polling');
      cancelledRef.current = true;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [token, enabled, refreshInterval, fetchNotifications, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    fetchNotifications,
  };
}

export default useNotificationsHTTP;


